/* eslint-disable class-methods-use-this */
import type Vue from 'vue';
import { env } from './env';
import { TTIPoint, ExceptionCategory } from './enum-type';

interface ICATErrorModel {
  level?: 'error' | 'info' | 'warn' | 'debug';
  category?: string;
  tags?: any;
}

class Monitor {
  private static readonly TTI_STORAGE_KEY: string = 'tti_report_flag';

  private readonly config: object;

  private readonly extInfo: object;

  constructor(extInfo: object, isDebug: boolean) {
    this.extInfo = extInfo;
    this.config = {
      project: 'pay-deduction-part',
      pageUrl: `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
      devMode: isDebug,
      resource: {
        sampleApi: 1,
        delay: 0,
      },
      page: {
        auto: true,
        sample: 1,
        delay: 0,
        sensoryIndex: false,
      },
      error: {
        sample: 1,
        combo: false,
      },
      ajax: {
        flag: true,
        duration: 8000,
      },
      metric: {
        sample: 1,
        combo: true,
      },
      logan: {
        enable: false,
      },
      noScriptError: false,
      onErrorPush: (error: any) => {
        return error.updateTags({
          ...extInfo,
          ...Monitor.traceInfo,
        });
      },
    };

    this.start();
  }

  static get traceInfo() {
    return {
      payRequestId: env.payRequestId,
      mTraceId: env.traceId,
    };
  }

  captureMessage(error: string | SubErrorType, config = {} as ICATErrorModel) {
    console.error(error);
    if (window.Owl) {
      const baseConfig: ICATErrorModel = {
        level: 'info',
        category: 'custom_info',
      };

      window.Owl.addError(error, {
        ...baseConfig,
        ...config,
      });
    }
  }

  captureAjaxErrorMessage(
    name: string,
    msg: Record<string, any> = {},
    tags: Record<string, any> = {}
  ) {
    this.captureMessage(
      {
        name,
        msg,
      },
      {
        tags,
        level: 'error',
        category: 'ajaxError',
      }
    );
  }

  captureJSErrorMessage(
    name: string,
    msg: Record<string, any> = {},
    tags: Record<string, any> = {}
  ) {
    this.captureMessage(
      {
        name,
        msg,
      },
      {
        tags,
        level: 'error',
        category: 'jsError',
      }
    );
  }

  captureResourceInfoMessage(
    name: string,
    msg: Record<string, any> = {},
    tags: Record<string, any> = {}
  ) {
    this.captureMessage(
      {
        name,
        msg,
      },
      {
        tags,
        level: 'info',
        category: 'resourceError',
      }
    );
  }

  reportMetric(key: string, tags = {}, value?: any): void {
    if (window.Owl) {
      const metricManager = window.Owl.metricManager;
      metricManager.setTags({
        ...this.extInfo,
        ...tags,
      });
      metricManager.setMetric(key, value || 1);
      // 强制上报所有
      metricManager.report();
    }
  }

  reportPoint(position: number, duration?: number): void {
    if (window.Owl) {
      const param: CustomPoint = { position };
      if (duration) {
        param.duration = duration;
      }
      window.Owl.addPoint(param);
      window.Owl.sendPoints();
    }
  }

  reportTTI(position: number = TTIPoint.position, duration?: number): void {
    try {
      if (
        !window.sessionStorage ||
        window.sessionStorage.getItem(Monitor.TTI_STORAGE_KEY) === '1'
      ) {
        return;
      }
      this.reportPoint(position, duration);
      window.sessionStorage.setItem(Monitor.TTI_STORAGE_KEY, '1');
    } catch (error) {
      this.captureJSErrorMessage(`上报 TTI 异常`, { error });
    }
  }

  vueErrorHandler(err: Error, vm: Vue, msg: string) {
    if (!err) {
      return;
    }
    // 为保证报错在控制台展示
    console.error(err);
    const error = {
      name: `${ExceptionCategory.VueErrorHandler}${msg}${err.name}`,
      msg: { res: err.stack ?? err.message, url: err.name }, // 只上报类型，vm 里的数据由于数据量大不再上报
    };
    const opts = {
      category: 'jsError',
    };
    if (window.Owl) {
      window.Owl.addError(error, {
        ...opts,
      });
    }
  }

  private start() {
    if (window.Owl) {
      window.Owl.config(this.config);
    }
  }
}

const info = {
  ...env.commonParams,
  entry: env.entry,
  mtPlanId: env.mtPlanId,
  hybridOnclickpayVersion: env.hybridVersion,
  openPageStyle: env.openPageStyle,
};

export const monitor = new Monitor(info, env.runtime > 0);
